import React, { useEffect, useState } from 'react';
import { Spin, Flex, Card, Typography, Tag, Empty } from 'antd';
import { useHistory } from 'react-router-dom';
import { mdiCurrencyUsd, mdiWeb, mdiEmail, mdiRoutes } from '@mdi/js';

import JourneyIdsFormatter from 'modules/ReportModule/formatters/JourneyIdsFormatter';
import AutomaticDrawer from 'components/Drawers/AutomaticDrawer';
import useQueryParams from 'hooks/useQueryParams';
import ninjaApi from 'store/redux/apis';
import P from 'router/paths';
import { NameDefinition } from 'components/NameDefinition';

import { DealStatusFormatter } from 'modules/CRMModule/formatters';
import { DateFormatter, IntegrationFormatter } from 'components/tables/NinjaTable/formatters';
import getFormattedValue from 'store/redux/utils/getFormattedValue';
import {
  AdFormatter,
  AdsetFormatter,
  UrlFormatter,
} from 'components/tables/NinjaTable/formatters/sourceFormatters';
import CrmInfoRow from '../components/CrmInfoRow';
import CrmInfoSection from '../components/CrmInfoSection';
import CrmFilterableField from '../components/CrmFilterableField';
import CrmPersonalInformation from '../components/CrmPersonalInformation';

const { Title, Text } = Typography;

const DealInfoDrawer = (props) => {
  const { searchParams, removeParams, setUrlParams } = useQueryParams();
  const { deal_id } = searchParams;
  const history = useHistory();

  const [fetchDeal, { isLoading }] = ninjaApi.useLazyGetSingleDealQuery();
  const [dealData, setDealData] = useState(null);

  useEffect(() => {
    if (deal_id) {
      fetchDeal({ id: deal_id })
        .unwrap()
        .then((data) => {
          setDealData(data.data);
        })
        .catch(() => setDealData(null));
    }
  }, [deal_id]);

  const filterDeal = (key, value) => {
    history.push(P.CRM.DEALS);
    removeParams(['deal_id', 'date_from', 'date_to']);
    setUrlParams({ [key]: value });
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify='center' align='center' style={{ minHeight: 400 }}>
          <Spin size='large' tip='Loading deal information...' />
        </Flex>
      );
    }

    if (!dealData) {
      return <Empty description='Deal not found' style={{ marginTop: 100 }} />;
    }

    return (
      <div style={{ padding: '8px 0' }}>
        <Card className='bg-orangeGradient mb-6 text-white'>
          <Flex justify='space-between' align='center'>
            <Flex vertical gap='small' className='min-w-72'>
              <Title level={3} style={{ color: 'white', margin: 0 }}>
                {dealData.name || 'Unnamed Deal'}
              </Title>
              <Flex size='middle' className='w-full'>
                <Tag bordered={false} className='w-fit flex items-center'>
                  <Text className='text-xs font-medium text-color-medium mr-1'>Deal ID:</Text>
                  <span className='font-medium'>{dealData.id}</span>
                </Tag>
                {dealData.status && (
                  <div className='relative w-full bg-white rounded-md'>
                    <DealStatusFormatter status={dealData.status} editable={false} />
                  </div>
                )}
              </Flex>
            </Flex>
            <div>
              <IntegrationFormatter className='!w-12 !h-12' row={dealData} />
            </div>
          </Flex>
        </Card>

        <CrmInfoSection title='Financial Information' icon={mdiCurrencyUsd}>
          <CrmInfoRow label='Value'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.value, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Profit'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.profit, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='COGS'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.cogs, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Discount'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.discount, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Shipping'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.shipping, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Transaction Fee'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.transaction_fee, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Taxes'>
            <Text strong className='text-sm'>
              {getFormattedValue(dealData.taxes, dealData.currency)}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow label='Currency'>
            <Text strong className='text-sm'>
              {dealData.currency || '-'}
            </Text>
          </CrmInfoRow>
        </CrmInfoSection>
        {dealData.url && (
          <CrmInfoSection title='Campaign Information' icon={mdiWeb}>
            <CrmInfoRow label='URL/Ad Name'>
              {dealData.url?.name ? (
                <UrlFormatter row={dealData} />
              ) : (
                <Text type='secondary'>-</Text>
              )}
            </CrmInfoRow>
            {dealData.url?.ad?.id && (
              <CrmInfoRow label='Ad'>
                <AdFormatter row={dealData?.url} />
              </CrmInfoRow>
            )}
            {dealData.url?.ad?.adset?.id && (
              <CrmInfoRow label='Adset'>
                <AdsetFormatter row={dealData?.url.ad} />
              </CrmInfoRow>
            )}
            {dealData.url?.ad?.adset?.campaign && (
              <CrmInfoRow label='Campaign'>
                <span className='bg-info-background text-color-black font-normal rounded-2xl p-1 px-2 text-sm'>
                  {dealData.url.ad.adset.campaign.name || '-'}
                </span>
              </CrmInfoRow>
            )}
          </CrmInfoSection>
        )}

        <CrmInfoSection title='Activity & Timeline' icon={mdiRoutes}>
          <CrmInfoRow label='Journey' span={24}>
            {dealData.session_id !== null ? (
              <JourneyIdsFormatter row={dealData} />
            ) : (
              <Text type='secondary'>No journey data</Text>
            )}
          </CrmInfoRow>
          <CrmInfoRow label='Synced At'>
            {dealData.created_at ? (
              <DateFormatter defaultValue={dealData.created_at} />
            ) : (
              <Text type='secondary'>-</Text>
            )}
          </CrmInfoRow>
          <CrmInfoRow label='Order Placed'>
            {dealData.date ? (
              <DateFormatter defaultValue={dealData.date} />
            ) : (
              <Text type='secondary'>-</Text>
            )}
          </CrmInfoRow>
        </CrmInfoSection>

        <CrmPersonalInformation
          title='Contact Information'
          data={{ ...dealData?.contact, viewed_products: dealData?.viewed_products }}
        />

        <CrmInfoSection title='Contact Details' icon={mdiEmail}>
          <CrmInfoRow label='Email'>
            <CrmFilterableField
              value={dealData.contact?.email}
              filterKey='email'
              onFilter={filterDeal}
            />
          </CrmInfoRow>
          <CrmInfoRow label='Phone'>
            <CrmFilterableField
              value={dealData.contact?.phone}
              filterKey='phone'
              onFilter={filterDeal}
            />
          </CrmInfoRow>
          <CrmInfoRow label='Emergency Phone'>
            <Text strong className='text-sm'>
              {dealData.contact?.emergency_phone || '-'}
            </Text>
          </CrmInfoRow>
          <CrmInfoRow
            label={
              <NameDefinition
                textSize='!font-normal'
                name='LTV Value'
                definiton='The total sum of all deal values associated with a contact.'
              />
            }
          >
            <Text className='text-sm font-bold'>
              {getFormattedValue(dealData?.contact?.ltv_contact_value || '-')}
            </Text>
          </CrmInfoRow>
        </CrmInfoSection>
      </div>
    );
  };

  return (
    <AutomaticDrawer
      size='large'
      title={
        <Flex align='center' gap='small'>
          <span>Deal Details</span>
        </Flex>
      }
      {...props}
    >
      {renderContent()}
    </AutomaticDrawer>
  );
};

DealInfoDrawer.requiredParams = {
  deal_id: (p) => parseInt(p) > 0,
};

export default DealInfoDrawer;
